import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { useAsyncCallback, useAsync } from 'react-async-hook';
import { FinanceProvider } from '@upstart/harmony/lib/legos/constants';

import dealsActions from '../../redux/deals/actions';
import dealerActions from '../../redux/dealers/actions';
import LoanStatusList from '../../components/LoanStatusList';
import SidebarContainer from '../SidebarContainer';
import './UpstartLoanStatus.scss';
import FinanceApplicationService from '../../services/FinanceApplicationService';
import { UpstartLoanStatusHeader } from './UpstartLoanStatusHeader';
import { getStatus } from '../../components/LoanStatusList/LoanStatusList';
import { SuperFlatBanner } from '../Banners/SuperFlatBanner';

function UpstartDealerLoanStatus(props) {
  const { actions, dealer, dealers } = props;

  const [startDate, setStartDate] = useState(moment().startOf('day'));
  const [endDate, setEndDate] = useState(moment().endOf('day'));
  const [searchTerm, setSearchTerm] = useState('');
  const [loanStatusList, setLoanStatusList] = useState([]);

  const applyRange = (newRange) => {
    setStartDate(moment(newRange.startDate));
    setEndDate(moment(newRange.endDate));
  };

  useEffect(() => {
    // If dealers.dealer is not set,
    // Fetch it now based on currentUserDealer
    if (!dealers?.dealer.id && dealer.id) {
      actions.dealerRequest({ dealerId: dealer.id });
    }
  }, [actions, dealers, dealer]);

  const asyncApplications = useAsync(
    (...args) =>
      dealer?.id
        ? FinanceApplicationService.getApplicationsForDealer(...args)
        : null,
    [
      dealer.id,
      FinanceProvider.UPSTART,
      moment(startDate).toISOString(),
      moment(endDate).toISOString(),
    ],
  );
  const financeApplications = asyncApplications.result;

  const asyncApplicationStatuses = useAsyncCallback(
    FinanceApplicationService.getApplicationStatuses,
  );
  const getFinanceApplicationStatuses = async (ids) => {
    try {
      await asyncApplicationStatuses.execute(ids);
    } catch (e) {
      // do nothing
    }
  };

  useEffect(() => {
    if (financeApplications) {
      const ids = [
        ...new Set(
          financeApplications.map((item) => item.providerApplicationId),
        ),
      ];
      getFinanceApplicationStatuses(ids);
    }
  }, [financeApplications]);

  const findStatusFromApplicationId = (applicationId, statuses) =>
    (statuses || []).find((s) => applicationId === s.applicationId) || {};

  const applicationStatuses = asyncApplicationStatuses.result;

  // handle creating loan status and searching
  useEffect(() => {
    const filteredLoanStatusList = [];

    for (const financeApplication of financeApplications || []) {
      const status = findStatusFromApplicationId(
        financeApplication.providerApplicationId,
        applicationStatuses,
      );

      const loanStatus = {
        financeApplication,
        status,
      };

      if (searchTerm) {
        const searchTermLC = searchTerm.toLowerCase();
        const { car } = financeApplication;
        const { applicant } = financeApplication.deal;
        const applicantName =
          `${applicant.firstName} ${applicant.lastName}`.toLowerCase();
        if (
          applicantName.includes(searchTermLC) ||
          (status?.contractNumber || '').toLowerCase().includes(searchTermLC) ||
          car.name?.toLowerCase().includes(searchTermLC) ||
          getStatus(financeApplication, status.title || '')
            .toLowerCase()
            .includes(searchTermLC)
        ) {
          filteredLoanStatusList.push(loanStatus);
        }
      } else {
        filteredLoanStatusList.push(loanStatus);
      }
    }

    setLoanStatusList(filteredLoanStatusList);
  }, [financeApplications, applicationStatuses, searchTerm]);

  const isFetching =
    asyncApplicationStatuses.loading || asyncApplications.loading;
  const isError = asyncApplicationStatuses.error || asyncApplications.error;

  return (
    <SidebarContainer>
      {props.user?.id && dealer && (
        <SuperFlatBanner dealer={dealer} userId={props.user.id} />
      )}
      <div className="dashboard-table">
        <div className="loan-status-dashboard-wrapper">
          <UpstartLoanStatusHeader
            startDate={startDate}
            endDate={endDate}
            applyRange={applyRange}
            searchTerm={searchTerm}
            onSearch={setSearchTerm}
          />

          <LoanStatusList
            loanStatusList={loanStatusList}
            error={isError}
            isFetching={isFetching}
            isAllLoanStatus={false}
          />
        </div>
      </div>
    </SidebarContainer>
  );
}

UpstartDealerLoanStatus.propTypes = {
  dealer: PropTypes.object,
  dealers: PropTypes.object,
  actions: PropTypes.object,
};

const mapStateToProps = ({ deals, user, dealers }) => ({
  deals,
  user: user.currentUser,
  dealer: user.currentUserDealer,
  dealers,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...dealsActions,
      ...dealerActions,
    },
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UpstartDealerLoanStatus);
